import { useEffect, useState } from 'react';
import { SPORTS } from '../data/constants';
import RecordDetail from './RecordDetail';
import { BJJ_BELTS, beltLabel } from './BjjPromotionSummary';
import { shareOrDownloadCanvas } from '../utils/shareCanvas';

function downloadPromotionShareImage(p) {
    const canvas = document.createElement('canvas');
    canvas.width = 1080;
    canvas.height = 1350;
    const ctx = canvas.getContext('2d');
    const belt = BJJ_BELTS.find(b => b.key === p.belt) || BJJ_BELTS[0];
    const stripes = Math.max(0, Math.min(4, +p.stripe || 0));

    ctx.fillStyle = '#0f0f0f';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const grad = ctx.createRadialGradient(820, 220, 60, 820, 220, 720);
    grad.addColorStop(0, `${belt.color}88`);
    grad.addColorStop(1, 'rgba(15,15,15,0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const text = (value, x, y, size, color = '#f4f4f4', weight = 700, align = 'left') => {
        ctx.fillStyle = color;
        ctx.font = `${weight} ${size}px "DM Sans", "Apple SD Gothic Neo", sans-serif`;
        ctx.textAlign = align;
        ctx.fillText(value, x, y);
    };
    const roundRect = (x, y, w, h, r) => {
        ctx.beginPath();
        ctx.moveTo(x + r, y);
        ctx.arcTo(x + w, y, x + w, y + h, r);
        ctx.arcTo(x + w, y + h, x, y + h, r);
        ctx.arcTo(x, y + h, x, y, r);
        ctx.arcTo(x, y, x + w, y, r);
        ctx.closePath();
    };
    const wrap = (value, x, y, maxWidth, lineHeight, size, color = '#d8d8d8', weight = 500) => {
        ctx.fillStyle = color;
        ctx.font = `${weight} ${size}px "DM Sans", "Apple SD Gothic Neo", sans-serif`;
        const words = String(value || '').split(' ');
        const lines = [];
        let line = '';
        words.forEach(word => {
            const next = line ? `${line} ${word}` : word;
            if (ctx.measureText(next).width > maxWidth && line) {
                lines.push(line);
                line = word;
            } else line = next;
        });
        if (line) lines.push(line);
        lines.slice(0, 4).forEach((l, i) => ctx.fillText(l, x, y + i * lineHeight));
    };

    text('JIU-JITSU PROMOTION', 82, 150, 30, '#9b8cff', 800);
    text(`${belt.label} ${stripes}그랄`, 82, 285, 92, '#f4f4f4', 800);
    text('승급했어요! 🎉', 82, 380, 64, '#f4f4f4', 800);

    roundRect(82, 470, 916, 160, 24);
    ctx.fillStyle = belt.color;
    ctx.fill();
    ctx.fillStyle = '#050505';
    roundRect(720, 492, 210, 116, 10);
    ctx.fill();
    for (let i = 0; i < 4; i++) {
        ctx.fillStyle = i < stripes ? '#fff' : 'rgba(255,255,255,0.18)';
        roundRect(748 + i * 42, 520, 18, 62, 4);
        ctx.fill();
    }

    const details = [
        ['DATE', p.date],
        ['PLACE', p.place],
    ].filter(([, v]) => v);
    details.forEach(([label, value], i) => {
        const x = 82 + i * 460;
        text(label, x, 760, 26, '#777', 700);
        wrap(value, x, 820, 390, 46, 38, '#f4f4f4', 700);
    });

    if (p.message) {
        text('NOTE', 82, 980, 26, '#777', 700);
        wrap(p.message, 82, 1040, 900, 52, 38, '#eaeaea', 500);
    }
    text('WORKOUT JOURNAL', 82, 1268, 26, '#9b8cff', 800);

    shareOrDownloadCanvas(
        canvas,
        `bjj-promotion-${p.date}.png`,
        '주짓수 승급 기록',
        `${belt.label} ${stripes}그랄로 승급했어요!`
    );
}

function PromotionDetail({ promotion, onBack, onSave, onDelete }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(promotion);

    const save = () => {
        if (!draft.date || !draft.place?.trim() || !draft.instructor?.trim()) return;
        onSave({ ...draft, place: draft.place.trim(), instructor: draft.instructor.trim(), message: (draft.message || '').trim(), stripe: +draft.stripe || 0 });
        setEditing(false);
    };

    return (
        <>
            <button className="back-btn" onClick={onBack}>← 돌아가기</button>
            <div className="detail-panel">
                <div className="detail-head">주짓수 승급 · {promotion.date}</div>
                {editing ? (
                    <>
                        <div className="form-group"><label className="form-label">승급일</label><input className="form-input" type="date" value={draft.date} onChange={e => setDraft({ ...draft, date: e.target.value })} /></div>
                        <div className="form-group"><label className="form-label">승급 장소</label><input className="form-input" value={draft.place || ''} onChange={e => setDraft({ ...draft, place: e.target.value })} /></div>
                        <div className="metric-grid">
                            <div className="form-group"><label className="form-label">벨트</label><select className="form-input" value={draft.belt} onChange={e => setDraft({ ...draft, belt: e.target.value })}>{BJJ_BELTS.map(b => <option key={b.key} value={b.key}>{b.label}</option>)}</select></div>
                            <div className="form-group"><label className="form-label">그랄</label><select className="form-input" value={draft.stripe} onChange={e => setDraft({ ...draft, stripe: e.target.value })}>{[0, 1, 2, 3, 4].map(n => <option key={n} value={n}>{n}그랄</option>)}</select></div>
                        </div>
                        <div className="form-group"><label className="form-label">승급 지도자</label><input className="form-input" value={draft.instructor || ''} onChange={e => setDraft({ ...draft, instructor: e.target.value })} /></div>
                        <div className="form-group"><label className="form-label">공개 범위</label><select className="form-input" value={draft.visibility || 'public'} onChange={e => setDraft({ ...draft, visibility: e.target.value })}><option value="private">내 피드에만</option><option value="friends">친구 공개</option><option value="public">전체 공개</option></select></div>
                        <div className="form-group"><label className="form-label">승급 소감</label><textarea className="form-input" rows={4} value={draft.message || ''} onChange={e => setDraft({ ...draft, message: e.target.value })} /></div>
                    </>
                ) : (
                    <>
                        <div className="detail-row"><span className="detail-key">자격</span><span className="detail-val">{beltLabel(promotion.belt)} {+promotion.stripe || 0}그랄</span></div>
                        <div className="detail-row"><span className="detail-key">승급 장소</span><span className="detail-val">{promotion.place}</span></div>
                        <div className="detail-row"><span className="detail-key">승급 지도자</span><span className="detail-val">{promotion.instructor}</span></div>
                        <div className="detail-row"><span className="detail-key">공개 범위</span><span className="detail-val">{promotion.visibility === 'public' ? '전체 공개' : promotion.visibility === 'friends' ? '친구 공개' : '내 피드에만'}</span></div>
                        {promotion.message && <div className="diary-text">{promotion.message}</div>}
                    </>
                )}
            </div>
            <div className="action-row">
                {editing ? <button className="action-btn" onClick={save}>저장하기</button> : <button className="action-btn" onClick={() => setEditing(true)}>수정하기</button>}
                <button className="action-btn" onClick={() => downloadPromotionShareImage(promotion)}>공유하기</button>
                <button className="action-btn danger" onClick={onDelete}>삭제</button>
            </div>
        </>
    );
}

export default function Calendar({ records, setRecords, weights, promotions = [], setPromotions, templates, setTemplates, showToast, setTab, setPendingEdit, setPendingDate, deletePostForRecord, updatePromotionPost, deletePromotionPostForPromotion }) {
    const initialParams = new URLSearchParams(window.location.search);
    const initialDate = initialParams.get('date');
    const initialMonth = initialParams.get('month');
    const [month, setMonth] = useState(() => {
        if (initialMonth && /^\d{4}-\d{2}$/.test(initialMonth)) {
            const [yy, mm] = initialMonth.split('-').map(Number);
            return new Date(yy, mm - 1, 1);
        }
        if (initialDate && /^\d{4}-\d{2}-\d{2}$/.test(initialDate)) {
            const [yy, mm] = initialDate.split('-').map(Number);
            return new Date(yy, mm - 1, 1);
        }
        return new Date();
    });
    const [selectedDate, setSelectedDate] = useState(initialDate || null);
    const [detailIdx, setDetailIdx] = useState(null);
    const [promotionIdx, setPromotionIdx] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [routineDraft, setRoutineDraft] = useState(null);
    const [routineName, setRoutineName] = useState('');
    const [recordQuery, setRecordQuery] = useState('');
    const [sportFilter, setSportFilter] = useState('all');

    const writeCalUrl = (params = {}) => {
        const url = new URL(window.location.href);
        url.searchParams.set('tab', 'cal');
        ['date', 'record', 'promotion', 'month'].forEach(key => url.searchParams.delete(key));
        Object.entries(params).forEach(([key, value]) => {
            if (value) url.searchParams.set(key, value);
        });
        window.history.pushState({}, '', `${url.pathname}${url.search}`);
    };

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const recordId = params.get('record');
        const promotionId = params.get('promotion');
        if (recordId && detailIdx === null) {
            const idx = recordId.startsWith('idx-')
                ? +recordId.slice(4)
                : records.findIndex(r => (r.recordId || '') === recordId);
            if (idx >= 0) setDetailIdx(idx);
        }
        if (promotionId && promotionIdx === null) {
            const idx = promotions.findIndex(p => (p.id || '') === promotionId);
            if (idx >= 0) setPromotionIdx(idx);
        }
    }, [records, promotions, detailIdx, promotionIdx]);

    useEffect(() => {
        const onPop = () => {
            const params = new URLSearchParams(window.location.search);
            const date = params.get('date');
            const monthParam = params.get('month');
            setDetailIdx(null);
            setPromotionIdx(null);
            setSelectedDate(date || null);
            if (date && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
                const [yy, mm] = date.split('-').map(Number);
                setMonth(new Date(yy, mm - 1, 1));
            } else if (monthParam && /^\d{4}-\d{2}$/.test(monthParam)) {
                const [yy, mm] = monthParam.split('-').map(Number);
                setMonth(new Date(yy, mm - 1, 1));
            }
        };
        window.addEventListener('popstate', onPop);
        return () => window.removeEventListener('popstate', onPop);
    }, []);

    const addOnDate = (date) => {
        setPendingDate(date);
        setTab('add');
    };

    const createRoutineFromRecord = (r, name) => {
        const next = {
            name,
            sport: r.sport,
            gi: r.gi || '',
            strType: r.strType || '',
            wrType: r.wrType || '',
            focus: r.focus || '',
            sparring: r.sparring || false,
            duration: r.duration || '',
            exercises: r.exercises?.length
                ? r.exercises.map(ex => ({
                    name: ex.name || '',
                    muscleGroup: ex.muscleGroup || '',
                    sets: (ex.sets || []).map(s => ({ ...s })),
                }))
                : undefined,
            strikingDrills: r.strikingDrills || [],
            drillTime: r.drillTime || '',
        };
        setTemplates([...(templates || []), next]);
        showToast('루틴 운동을 생성했어요');
    };

    const openRoutineModal = (r) => {
        const sportName = SPORTS[r.sport]?.name || r.sport || '운동';
        setRoutineName(`${sportName} 루틴`);
        setRoutineDraft(r);
    };

    const saveRoutineDraft = () => {
        const name = routineName.trim();
        if (!name) {
            showToast('루틴 이름을 입력해주세요');
            return;
        }
        createRoutineFromRecord(routineDraft, name);
        setRoutineDraft(null);
        setRoutineName('');
    };

    const savePromotionEdit = (nextPromotion) => {
        const next = [...promotions];
        next[promotionIdx] = nextPromotion;
        setPromotions(next);
        if (updatePromotionPost && nextPromotion.postId) {
            updatePromotionPost(nextPromotion.postId, nextPromotion, nextPromotion.visibility || 'public')
                .catch(() => showToast('승급 기록은 수정됐지만 피드 수정에 실패했어요'));
        }
        showToast('승급 기록을 수정했어요');
    };

    if (promotionIdx !== null) {
        return (
            <>
                <PromotionDetail
                    promotion={promotions[promotionIdx]}
                    onBack={() => {
                        const date = promotions[promotionIdx]?.date;
                        setPromotionIdx(null);
                        if (date) {
                            setSelectedDate(date);
                            writeCalUrl({ date });
                        } else {
                            writeCalUrl({ month: `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, '0')}` });
                        }
                    }}
                    onSave={savePromotionEdit}
                    onDelete={() => setConfirmDelete(true)}
                />
                {confirmDelete && (
                    <div className="confirm-backdrop">
                        <div className="confirm-panel">
                            <div className="confirm-title">승급 기록을 삭제할까요?</div>
                            <div className="confirm-desc">피드에 올라간 승급글도 함께 정리돼요.</div>
                            <div className="confirm-actions">
                                <button className="action-btn" onClick={() => setConfirmDelete(false)}>취소</button>
                                <button className="action-btn danger" onClick={() => {
                                    const promotion = promotions[promotionIdx];
                                    const next = [...promotions]; next.splice(promotionIdx, 1);
                                    setPromotions(next);
                                    if (deletePromotionPostForPromotion) {
                                        deletePromotionPostForPromotion(promotion).catch(() => showToast('승급 기록은 삭제됐지만 피드 삭제에 실패했어요'));
                                    }
                                    showToast('삭제했어요');
                                    setPromotionIdx(null);
                                    setConfirmDelete(false);
                                }}>삭제</button>
                            </div>
                        </div>
                    </div>
                )}
            </>
        );
    }

    if (detailIdx !== null) {
        return (
            <>
                <RecordDetail
                    record={records[detailIdx]}
                    onBack={() => {
                        const date = records[detailIdx]?.date;
                        setDetailIdx(null);
                        if (date) {
                            setSelectedDate(date);
                            writeCalUrl({ date });
                        } else {
                            writeCalUrl({ month: `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, '0')}` });
                        }
                    }}
                    onEdit={() => { setPendingEdit(detailIdx); setTab('add'); }}
                    onCreateRoutine={() => openRoutineModal(records[detailIdx])}
                    onDelete={() => {
                        setConfirmDelete(true);
                    }}
                />
                {routineDraft && (
                    <div className="confirm-backdrop">
                        <div className="confirm-panel">
                            <div className="confirm-title">루틴 이름</div>
                            <div className="confirm-desc">이 기록을 다시 사용할 수 있는 루틴으로 저장해요.</div>
                            <input
                                className="form-input"
                                value={routineName}
                                onChange={e => setRoutineName(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && saveRoutineDraft()}
                                autoFocus
                            />
                            <div className="confirm-actions" style={{ marginTop: 12 }}>
                                <button className="action-btn" onClick={() => { setRoutineDraft(null); setRoutineName(''); }}>취소</button>
                                <button className="action-btn" onClick={saveRoutineDraft}>저장</button>
                            </div>
                        </div>
                    </div>
                )}
                {confirmDelete && (
                    <div className="confirm-backdrop">
                        <div className="confirm-panel">
                            <div className="confirm-title">기록을 삭제할까요?</div>
                            <div className="confirm-desc">삭제한 기록은 되돌릴 수 없어요.</div>
                            <div className="confirm-actions">
                                <button className="action-btn" onClick={() => setConfirmDelete(false)}>취소</button>
                                <button className="action-btn danger" onClick={() => {
                                    const record = records[detailIdx];
                                    const next = [...records]; next.splice(detailIdx, 1);
                                    setRecords(next);
                                    if (deletePostForRecord) {
                                        deletePostForRecord(record).catch(err => {
                                            console.error('소셜 게시물 삭제 실패:', err);
                                            showToast('기록은 삭제됐지만 소셜 게시물 삭제에 실패했어요');
                                        });
                                    }
                                    showToast('삭제했어요'); setDetailIdx(null); setConfirmDelete(false);
                                }}>삭제</button>
                            </div>
                        </div>
                    </div>
                )}
            </>
        );
    }

    if (selectedDate) {
        const dayRecs = records.filter(r => r.date === selectedDate);
        const dayPromotions = promotions.filter(p => p.date === selectedDate);
        const dayWeight = (weights || []).find(w => w.date === selectedDate);
        const label = selectedDate.replace(/-/g, '.');

        return (
            <>
                <button className="back-btn" onClick={() => {
                    setSelectedDate(null);
                    writeCalUrl({ month: `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, '0')}` });
                }}>← 달력</button>
                <p className="section-label">{label}</p>

                {dayWeight && (
                    <div className="export-card" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 20 }}>⚖️</span>
                        <div>
                            <div style={{ fontSize: 15, fontWeight: 600, fontFamily: 'DM Mono,monospace' }}>{dayWeight.val}kg</div>
                            <div style={{ fontSize: 11, color: 'var(--text3)' }}>체중</div>
                        </div>
                    </div>
                )}

                {dayRecs.length === 0 && dayPromotions.length === 0 && !dayWeight && (
                    <div className="empty-state">
                        <div className="empty-text">이날 기록이 없어요</div>
                        <button className="tpl-btn primary empty-action" style={{ marginTop: '20px' }} onClick={() => addOnDate(selectedDate)}>이 날짜에 기록하기</button>
                    </div>
                )}

                {dayPromotions.length > 0 && (
                    <div className="recent-list">
                        {dayPromotions.map(p => (
                            <div key={p.id} className="record-card" onClick={() => {
                                setPromotionIdx(promotions.indexOf(p));
                                writeCalUrl({ promotion: p.id });
                            }}>
                                <div className="record-head">
                                    <div className="record-sport">
                                        <div className="sport-dot" style={{ background: '#9b8cff' }} />
                                        <span className="record-name">주짓수 승급</span>
                                    </div>
                                    <span style={{ fontSize: 12, color: 'var(--text3)' }}>›</span>
                                </div>
                                <div className="record-tags">
                                    <span className="tag">{beltLabel(p.belt)} {+p.stripe || 0}그랄</span>
                                    {p.place && <span className="tag">{p.place}</span>}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {dayRecs.length > 0 && (
                    <div className="recent-list">
                        {dayRecs.map(r => (
                            <div key={records.indexOf(r)} className="record-card" onClick={() => {
                                setDetailIdx(records.indexOf(r));
                                writeCalUrl({ record: r.recordId || `idx-${records.indexOf(r)}` });
                            }}>
                                <div className="record-head">
                                    <div className="record-sport">
                                        <div className="sport-dot" style={{ background: SPORTS[r.sport]?.color || '#888' }} />
                                        <span className="record-name">{SPORTS[r.sport]?.name || r.sport}</span>
                                    </div>
                                    <span style={{ fontSize: 12, color: 'var(--text3)' }}>›</span>
                                </div>
                                <div className="record-tags">
                                    {r.duration && <span className="tag">{r.duration}분</span>}
                                    {r.sparring && <span className="tag">스파링</span>}
                                    {r.muscleGroup && <span className="tag">{r.muscleGroup}</span>}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                <button className="add-btn" style={{ marginTop: '20px' }} onClick={() => addOnDate(selectedDate)}>+ 이 날짜에 기록하기</button>
            </>
        );
    }

    const y = month.getFullYear();
    const m = month.getMonth();
    const first = new Date(y, m, 1).getDay();
    const lastDay = new Date(y, m + 1, 0).getDate();
    const toDateStr = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const today = toDateStr(new Date());
    const dayNames = ['일', '월', '화', '수', '목', '금', '토'];

    const days = [];
    for (let i = 0; i < first; i++) {
        const d = new Date(y, m, -(first - 1 - i));
        days.push({ d, other: true });
    }
    for (let i = 1; i <= lastDay; i++) days.push({ d: new Date(y, m, i), other: false });
    while (days.length % 7) {
        const d = new Date(y, m + 1, days.length - first - lastDay + 1);
        days.push({ d, other: true });
    }

    const changeMonth = (delta) => {
        const next = new Date(month);
        next.setMonth(next.getMonth() + delta);
        setMonth(next);
        writeCalUrl({ month: `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}` });
    };

    const monthRecs = records.filter(r => r.date.startsWith(`${y}-${String(m + 1).padStart(2, '0')}`));
    const monthPromotions = promotions.filter(p => p.date?.startsWith(`${y}-${String(m + 1).padStart(2, '0')}`));
    const monthWeights = (weights || []).filter(w => w.date?.startsWith(`${y}-${String(m + 1).padStart(2, '0')}`));
    const monthSports = [...new Set(monthRecs.map(r => r.sport).filter(Boolean))];
    const q = recordQuery.trim().toLowerCase();
    const matchesRecord = (r) => {
        const sportName = SPORTS[r.sport]?.name || r.sport || '';
        const exercises = (r.exercises || []).map(ex => `${ex.name || ''} ${ex.muscleGroup || ''}`).join(' ');
        const techniques = Array.isArray(r.techniques)
            ? r.techniques.map(t => `${t.name || ''} ${t.position || ''} ${t.category || ''}`).join(' ')
            : (r.techniques || '');
        const haystack = [sportName, r.focus, r.diary, exercises, techniques, r.cardioType, r.gi, r.strType].filter(Boolean).join(' ').toLowerCase();
        return (!q || haystack.includes(q)) && (sportFilter === 'all' || r.sport === sportFilter);
    };
    const matchesPromotion = (p) => {
        const haystack = ['주짓수 승급', beltLabel(p.belt), p.place, p.message].filter(Boolean).join(' ').toLowerCase();
        return sportFilter === 'all' && (!q || haystack.includes(q));
    };
    const matchesWeight = (w) => {
        const haystack = ['체중', '몸무게', `${w.val || ''}kg`, w.date].join(' ').toLowerCase();
        return sportFilter === 'all' && (!q || haystack.includes(q));
    };
    const visibleMonthRecs = monthRecs.filter(matchesRecord);
    const visibleMonthPromotions = monthPromotions.filter(matchesPromotion);
    const visibleMonthWeights = monthWeights.filter(matchesWeight);
    const visibleMonthItems = [
        ...visibleMonthWeights.map(w => ({ type: 'weight', date: w.date, item: w })),
        ...visibleMonthPromotions.map(p => ({ type: 'promotion', date: p.date, item: p })),
        ...visibleMonthRecs.map(r => ({ type: 'record', date: r.date, item: r })),
    ].sort((a, b) => new Date(b.date) - new Date(a.date));

    return (
        <>
            <div className="cal-month-nav">
                <button className="nav-arrow" onClick={() => changeMonth(-1)}>‹</button>
                <div className="cal-jump-controls">
                    <select className="cal-jump-select" value={y} onChange={e => {
                        const next = new Date(+e.target.value, m, 1);
                        setMonth(next);
                        writeCalUrl({ month: `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}` });
                    }}>
                        {Array.from({ length: 11 }, (_, i) => y - 5 + i).map(year => <option key={year} value={year}>{year}년</option>)}
                    </select>
                    <select className="cal-jump-select" value={m} onChange={e => {
                        const next = new Date(y, +e.target.value, 1);
                        setMonth(next);
                        writeCalUrl({ month: `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}` });
                    }}>
                        {Array.from({ length: 12 }, (_, i) => <option key={i} value={i}>{i + 1}월</option>)}
                    </select>
                </div>
                <button className="nav-arrow" onClick={() => changeMonth(1)}>›</button>
            </div>

            <div className="cal-grid">
                {dayNames.map(n => <div key={n} className="cal-head">{n}</div>)}
                {days.map(({ d, other }, i) => {
                    const ds = toDateStr(d);
                    const recs = records.filter(r => r.date === ds);
                    const promos = promotions.filter(p => p.date === ds);
                    const itemCount = recs.length + promos.length;
                    return (
                        <div
                            key={i}
                            className={`cal-day ${ds === today ? 'today' : ''}`}
                            role="button"
                            tabIndex={0}
                            aria-label={`${ds} 기록 ${itemCount}개`}
                            onKeyDown={e => {
                                if (e.key !== 'Enter' && e.key !== ' ') return;
                                e.preventDefault();
                                setSelectedDate(ds);
                                setMonth(new Date(d.getFullYear(), d.getMonth(), 1));
                                writeCalUrl({ date: ds });
                            }}
                            onClick={() => {
                                setSelectedDate(ds);
                                setMonth(new Date(d.getFullYear(), d.getMonth(), 1));
                                writeCalUrl({ date: ds });
                            }}
                        >
                            <span className={`cal-num ${other ? 'other-month' : ''}`}>{d.getDate()}</span>
                            <div className="cal-dots">
                                {recs.slice(0, 3).map((r, j) => (
                                    <div key={j} className="cal-dot" style={{ background: SPORTS[r.sport]?.color || '#888' }} />
                                ))}
                                {promos.slice(0, Math.max(0, 3 - recs.length)).map((p, j) => (
                                    <div key={`p-${j}`} className="cal-dot" style={{ background: '#9b8cff' }} />
                                ))}
                            </div>
                            {itemCount > 3 && <div className="cal-more-count">+{itemCount - 3}</div>}
                        </div>
                    );
                })}
            </div>

            <div className="record-filter-panel">
                <input
                    className="form-input"
                    value={recordQuery}
                    onChange={e => setRecordQuery(e.target.value)}
                    placeholder="이번 달 기록 검색"
                    aria-label="이번 달 기록 검색"
                />
                <select
                    className="form-input record-filter-select"
                    value={sportFilter}
                    onChange={e => setSportFilter(e.target.value)}
                    aria-label="종목 필터"
                >
                    <option value="all">전체</option>
                    {monthSports.map(sport => (
                        <option key={sport} value={sport}>{SPORTS[sport]?.name || sport}</option>
                    ))}
                </select>
            </div>

            <p className="section-label">이번 달 기록</p>
            {monthRecs.length === 0 && monthPromotions.length === 0 && monthWeights.length === 0 ? (
                <div className="empty-state"><div className="empty-text">이달 기록 없음</div></div>
            ) : visibleMonthRecs.length === 0 && visibleMonthPromotions.length === 0 && visibleMonthWeights.length === 0 ? (
                <div className="empty-state"><div className="empty-text">검색 조건에 맞는 기록이 없어요</div></div>
            ) : (
                <div className="recent-list">
                    {visibleMonthItems.slice(0, 45).map(({ type, item }) => {
                        if (type === 'weight') {
                            return (
                                <div key={`weight-${item.date}`} className="record-card" onClick={() => {
                                    setSelectedDate(item.date);
                                    writeCalUrl({ date: item.date });
                                }}>
                                    <div className="record-head">
                                        <div className="record-sport">
                                            <div className="sport-dot" style={{ background: '#8fd3ff' }} />
                                            <span className="record-name">체중 기록</span>
                                        </div>
                                        <span className="record-date">{item.date}</span>
                                    </div>
                                    <div className="record-tags">
                                        <span className="tag">{item.val}kg</span>
                                    </div>
                                </div>
                            );
                        }
                        if (type === 'promotion') {
                            return (
                                <div key={item.id} className="record-card" onClick={() => {
                                    setPromotionIdx(promotions.indexOf(item));
                                    writeCalUrl({ promotion: item.id });
                                }}>
                                    <div className="record-head">
                                        <div className="record-sport">
                                            <div className="sport-dot" style={{ background: '#9b8cff' }} />
                                            <span className="record-name">주짓수 승급</span>
                                        </div>
                                        <span className="record-date">{item.date}</span>
                                    </div>
                                    <div className="record-tags">
                                        <span className="tag">{beltLabel(item.belt)} {+item.stripe || 0}그랄</span>
                                        {item.place && <span className="tag">{item.place}</span>}
                                    </div>
                                </div>
                            );
                        }
                        return (
                            <div key={records.indexOf(item)} className="record-card" onClick={() => {
                                setDetailIdx(records.indexOf(item));
                                writeCalUrl({ record: item.recordId || `idx-${records.indexOf(item)}` });
                            }}>
                                <div className="record-head">
                                    <div className="record-sport">
                                        <div className="sport-dot" style={{ background: SPORTS[item.sport]?.color || '#888' }} />
                                        <span className="record-name">{SPORTS[item.sport]?.name || item.sport}</span>
                                    </div>
                                    <span className="record-date">{item.date}</span>
                                </div>
                                <div className="record-tags">
                                    {item.duration && <span className="tag">{item.duration}분</span>}
                                    {item.sparring && <span className="tag">스파링</span>}
                                    {item.muscleGroup && <span className="tag">{item.muscleGroup}</span>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </>
    );
}
